import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import styled from "styled-components";
import Barcode from "react-barcode";
import { CurrencyFormat } from "utils/CurrencyFormat";
import ActionButton from "../../components/Button/ActionButton";
import Qrcode from "react-qr-code";
/**
 * Icons
 */
import { X } from "@phosphor-icons/react";

interface IQRCode {
  name: string;
  brands?: string;
  price: string | number;
  barcode: string;
  isOpenBarcode: boolean;
  setModalClose: () => void;
}

export default function BarcodeExport({
  name,
  price,
  brands,
  barcode,
  isOpenBarcode,
  setModalClose,
}: IQRCode) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [showBarcode, setShowBarcode] = useState(false);
  const [captureType, setCaptureType] = useState("qrcode");
  const [sizeType, setSizeType] = useState("large"); // "large" (G) ou "small" (p)

  // Função para capturar e baixar o QR Code ou Código de Barras
  const handleCapture = () => {
    if (targetRef.current) {
      html2canvas(targetRef.current)
        .then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.href = imgData;
          link.download = `${captureType}_${sizeType}_${name}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          console.error("Erro ao capturar elemento:", error);
        });
    }
  };

  const handleSelectType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const typeCheck = event.target.id;
    const [codeType, size] = typeCheck.split("-");

    setShowBarcode(codeType === "barcode");
    setCaptureType(codeType);
    setSizeType(size === "p" ? "small" : "large");
  };

  // Componente de etiqueta grande (G)
  const LargeLabel = () => (
    <PrintQrcode ref={targetRef}>
      <ContentBarcode>
        {showBarcode ? (
          <Barcode value={barcode} />
        ) : (
          <Qrcode value={name} size={90} />
        )}
      </ContentBarcode>
      <Infor>
        <ContentTitle>
          <TitleQr>{barcode}</TitleQr>
          <TitleName>{name}</TitleName>
          {brands && <TitleName>{brands}</TitleName>}
          <LabelPrice>
            {CurrencyFormat(parseFloat(price.toString()))}
          </LabelPrice>
        </ContentTitle>
      </Infor>
    </PrintQrcode>
  );

  // Componente de etiqueta pequena (p) para óculos
  const SmallLabel = () => (
    <PrintQrcodeSmall ref={targetRef}>
      <LeftSection>
        {showBarcode ? (
          <Barcode value={barcode} height={60} width={1} fontSize={10} />
        ) : (
          <Qrcode value={name} size={80} />
        )}
      </LeftSection>
      <RightSection>
        <SmallLabelName>{name}</SmallLabelName>
        {brands && <SmallLabelName>{brands}</SmallLabelName>}
        <LabelPrice>
          {CurrencyFormat(parseFloat(price.toString()))}
        </LabelPrice>
      </RightSection>
    </PrintQrcodeSmall>
  );

  if (isOpenBarcode) {
    return (
      <Container>
        <Content>
          <ContainerExport>
            <ButtonClose onClick={setModalClose}>
              <X size={24} />
            </ButtonClose>
          </ContainerExport>

          <ContainerCheck>
            <CheckBox
              type="radio"
              id="barcode-g"
              name="export-code"
              checked={captureType === "barcode" && sizeType === "large"}
              onChange={(e) => handleSelectType(e)}
            />
            <label htmlFor="barcode-g">Código de Barras (G)</label>

            <CheckBox
              type="radio"
              id="qrcode-g"
              name="export-code"
              checked={captureType === "qrcode" && sizeType === "large"}
              onChange={(e) => handleSelectType(e)}
            />
            <label htmlFor="qrcode-g">QRcodes (G)</label>
          </ContainerCheck>

          <ContainerCheck>
            <CheckBox
              type="radio"
              id="barcode-p"
              name="export-code"
              checked={captureType === "barcode" && sizeType === "small"}
              onChange={(e) => handleSelectType(e)}
            />
            <label htmlFor="barcode-p">Código de Barras (p)</label>

            <CheckBox
              type="radio"
              id="qrcode-p"
              name="export-code"
              checked={captureType === "qrcode" && sizeType === "small"}
              onChange={(e) => handleSelectType(e)}
            />
            <label htmlFor="qrcode-p">QRcodes (p)</label>
          </ContainerCheck>

          {/* Renderiza etiqueta grande ou pequena com base na seleção */}
          {sizeType === "large" ? <LargeLabel /> : <SmallLabel />}

          <ContainerExport>
            <ActionButton onClick={() => handleCapture()} name="Baixar" />
          </ContainerExport>
        </Content>
      </Container>
    );
  }
  return null;
}

const Container = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgb(0, 0, 0, 0.2);
  z-index: 1000;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  padding: 30px;
  border-radius: 4px;

  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContentTitle = styled.div``;

export const ContainerExport = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 10px;
`;

export const ContainerCheck = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

export const CheckBox = styled.input`
  width: 18px;
  height: 18px;
  appearance: none;
  cursor: pointer;

  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.gray.gray_100};

  &:checked {
    background-color: ${(props) => props.theme.colors.primary};
    border-color: ${(props) => props.theme.colors.primary};
  }

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
  }

  &::after {
    content: "";
    display: block;
    width: 8px;
    height: 8px;
    margin: 3px auto;
    color: ${(props) => props.theme.colors.white};
    opacity: 0;
    transition: opacity 0.2s ease;
  }
`;

// Estilo da etiqueta grande (G)
const PrintQrcode = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px dashed #ddd;
`;

// Estilo da etiqueta pequena (p)
const PrintQrcodeSmall = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px dashed #ddd;

  width: 370px; /* ~90mm em pixels */
`;

const LeftSection = styled.div`
  display: flex;
  padding-left: 4px;
  width: 100%;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const ContentBarcode = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TitleQr = styled.p`
  font-size: 16px;
`;

const Infor = styled.div`
  display: flex;
  gap: 16px;

  h1 {
    font-size: 24px;
    color: ${(props) => props.theme.colors.black};
  }
`;

export const TitleName = styled.p`
  max-width: 140px;
  word-wrap: break-word;
  font-size: 16px;
`;

export const ButtonClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
`;

// Estilos específicos para etiquetas pequenas
const LabelPrice = styled.h2`
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${(props) => props.theme.colors.black};
  `;

const SmallLabelName = styled.p`
  font-size: 18px;
  font-weight: 500;

  max-width: 200px;
  word-wrap: break-word;
`;